import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import bankAccountService from '../../services/bankAccountService';
import { BankAccount, BankTransaction } from '../../types';
import BankAdjustmentForm from './BankAdjustmentForm';

interface BankAccountDetailProps {
  account: BankAccount;
  onClose: () => void;
}

const thStyle: React.CSSProperties = {
  padding: '10px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  fontWeight: 500,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  borderBottom: '1px solid rgba(92,78,51,0.15)',
  whiteSpace: 'nowrap',
  textAlign: 'left',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  borderBottom: '1px solid rgba(241,245,249,1)',
  verticalAlign: 'middle',
};

const transactionTypeStyle: Record<string, React.CSSProperties> = {
  INCOME:   { background: 'rgba(90,201,140,0.12)', color: '#5ac98c', border: '1px solid rgba(90,201,140,0.25)' },
  EXPENSE:  { background: 'rgba(201,90,90,0.12)',  color: '#c95a5a', border: '1px solid rgba(201,90,90,0.25)' },
  TRANSFER: { background: 'rgba(90,140,201,0.12)', color: '#5a8cc9', border: '1px solid rgba(90,140,201,0.25)' },
  DEFAULT:  { background: 'rgba(120,120,140,0.15)', color: '#9a9ab0', border: '1px solid rgba(120,120,140,0.25)' },
};

const BankAccountDetail: React.FC<BankAccountDetailProps> = ({ account, onClose }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'balance' | 'transactions'>('balance');
  const [dateFilters, setDateFilters] = useState({
    startDate: '',
    endDate: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [showAdjustmentForm, setShowAdjustmentForm] = useState(false);

  const { data: balanceData, isLoading: balanceLoading } = useQuery({
    queryKey: ['bankAccountBalance', account.id],
    queryFn: () => bankAccountService.getAccountBalance(account.id)
  });

  const { data: transactionsData, isLoading: transactionsLoading } = useQuery({
    queryKey: ['bankAccountTransactions', account.id, currentPage, dateFilters.startDate, dateFilters.endDate],
    queryFn: () => bankAccountService.getAccountTransactions(account.id, {
      page: currentPage,
      limit: 20,
      ...(dateFilters.startDate && { startDate: dateFilters.startDate }),
      ...(dateFilters.endDate && { endDate: dateFilters.endDate })
    }),
    enabled: activeTab === 'transactions'
  });

  const formatCurrency = (amount: number) => {
    return `BD ${amount.toLocaleString('en-BH', { minimumFractionDigits: 3 })}`;
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-BH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTransactionTypeStyle = (type: string): React.CSSProperties => {
    return transactionTypeStyle[type] || transactionTypeStyle.DEFAULT;
  };

  const handleDateFilterChange = (field: 'startDate' | 'endDate', value: string) => {
    setDateFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1);
  };

  const clearDateFilters = () => {
    setDateFilters({ startDate: '', endDate: '' });
    setCurrentPage(1);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.72)',
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      zIndex: 50,
    }}>
      <div style={{
        background: '#ffffff',
        border: '1px solid rgba(92,78,51,0.25)',
        borderRadius: 6,
        maxWidth: 900,
        width: '100%',
        margin: '0 16px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid rgba(92,78,51,0.15)',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 22,
                fontWeight: 300,
                color: '#1c2033',
                letterSpacing: '0.02em',
              }}>
                {account.accountName}
              </div>
              <div style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 12,
                color: '#6b6a5c',
                marginTop: 4,
              }}>
                {account.bankName} &bull; {account.accountNumber}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                onClick={() => navigate(`/bank-accounts/${account.id}/reconciliation`)}
                style={{
                  padding: '7px 14px',
                  background: 'rgba(90,201,140,0.1)',
                  border: '1px solid rgba(90,201,140,0.3)',
                  borderRadius: 4,
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 9,
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  color: '#5ac98c',
                  cursor: 'pointer',
                }}
              >
                Reconcile
              </button>
              <button
                onClick={() => setShowAdjustmentForm(true)}
                style={{
                  padding: '7px 14px',
                  background: 'linear-gradient(135deg, #d97706, #b45309)',
                  border: 'none',
                  borderRadius: 4,
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 9,
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  color: '#1c2033',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                Add Adjustment
              </button>
              <button
                onClick={onClose}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6b6a5c',
                  padding: 4,
                  lineHeight: 1,
                }}
              >
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Account Info Grid */}
          <div style={{
            marginTop: 16,
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 16,
          }}>
            <div>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.2em', color: '#6b6a5c', textTransform: 'uppercase', marginBottom: 4 }}>Account Type</div>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#1c2033', textTransform: 'capitalize' }}>{account.accountType}</div>
            </div>
            <div>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.2em', color: '#6b6a5c', textTransform: 'uppercase', marginBottom: 4 }}>Status</div>
              <span style={{
                display: 'inline-block',
                padding: '3px 10px',
                borderRadius: 3,
                fontFamily: "'Inter', sans-serif",
                fontSize: 8,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                ...(account.isActive
                  ? { background: 'rgba(90,201,140,0.12)', color: '#5ac98c', border: '1px solid rgba(90,201,140,0.25)' }
                  : { background: 'rgba(201,90,90,0.12)', color: '#c95a5a', border: '1px solid rgba(201,90,90,0.25)' }
                ),
              }}>
                {account.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.2em', color: '#6b6a5c', textTransform: 'uppercase', marginBottom: 4 }}>Current Balance</div>
              <div style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 18,
                fontWeight: 300,
                color: (balanceData?.balance ?? account.balance) >= 0 ? '#5ac98c' : '#c95a5a',
              }}>
                {formatCurrency(balanceData?.balance ?? account.balance)}
              </div>
            </div>
            <div>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.2em', color: '#6b6a5c', textTransform: 'uppercase', marginBottom: 4 }}>Created</div>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#5b5a52' }}>{formatDate(account.createdAt)}</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ borderBottom: '1px solid rgba(92,78,51,0.15)', flexShrink: 0, display: 'flex', padding: '0 24px', gap: 0 }}>
          {(['balance', 'transactions'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '14px 4px',
                marginRight: 28,
                fontFamily: "'Inter', sans-serif",
                fontSize: 9,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === tab ? '2px solid #d97706' : '2px solid transparent',
                color: activeTab === tab ? '#d97706' : '#8a8778',
                cursor: 'pointer',
                transition: 'color 0.15s, border-color 0.15s',
              }}
            >
              {tab === 'balance' ? 'Balance Overview' : 'Recent Transactions'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>

          {/* Balance Tab */}
          {activeTab === 'balance' && (
            <div>
              {balanceLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 128 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    border: '2px solid rgba(92,78,51,0.25)',
                    borderTopColor: '#d97706',
                    animation: 'spin 0.8s linear infinite',
                  }} />
                </div>
              ) : balanceData ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
                  {/* Total Income */}
                  <div style={{
                    background: '#faf7ee',
                    border: '1px solid rgba(90,201,140,0.15)',
                    borderRadius: 6,
                    padding: 24,
                  }}>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#5ac98c', marginBottom: 12 }}>Total Income</div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 28, fontWeight: 300, color: '#5ac98c' }}>
                      {formatCurrency(balanceData.totalIncome)}
                    </div>
                  </div>

                  {/* Total Expenses */}
                  <div style={{
                    background: '#faf7ee',
                    border: '1px solid rgba(201,90,90,0.15)',
                    borderRadius: 6,
                    padding: 24,
                  }}>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#c95a5a', marginBottom: 12 }}>Total Expenses</div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 28, fontWeight: 300, color: '#c95a5a' }}>
                      {formatCurrency(balanceData.totalExpense)}
                    </div>
                  </div>

                  {/* Current Balance */}
                  <div style={{
                    background: '#faf7ee',
                    border: '1px solid rgba(92,78,51,0.25)',
                    borderRadius: 6,
                    padding: 24,
                  }}>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6a5c', marginBottom: 12 }}>Current Balance</div>
                    <div style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 28,
                      fontWeight: 300,
                      color: balanceData.balance >= 0 ? '#fbbf24' : '#c95a5a',
                    }}>
                      {formatCurrency(balanceData.balance)}
                    </div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 6 }}>
                      Last updated: {formatDate(balanceData.lastUpdated)}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#6b6a5c', padding: 32 }}>
                  Failed to load balance information
                </div>
              )}
            </div>
          )}

          {/* Transactions Tab */}
          {activeTab === 'transactions' && (
            <div>
              {/* Date Filter Controls */}
              <div style={{
                marginBottom: 20,
                padding: 16,
                background: '#faf7ee',
                border: '1px solid rgba(92,78,51,0.15)',
                borderRadius: 6,
              }}>
                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6a5c', marginBottom: 12 }}>
                  Filter Transactions
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
                  <div>
                    <label style={{ display: 'block', fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#6b6a5c', marginBottom: 6 }}>
                      From Date
                    </label>
                    <input
                      type="date"
                      value={dateFilters.startDate}
                      onChange={(e) => handleDateFilterChange('startDate', e.target.value)}
                      style={{
                        padding: '8px 12px',
                        background: '#f4efe2',
                        border: '1px solid rgba(92,78,51,0.25)',
                        borderRadius: 4,
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 12,
                        color: '#5b5a52',
                        outline: 'none',
                        colorScheme: 'light',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#6b6a5c', marginBottom: 6 }}>
                      To Date
                    </label>
                    <input
                      type="date"
                      value={dateFilters.endDate}
                      onChange={(e) => handleDateFilterChange('endDate', e.target.value)}
                      style={{
                        padding: '8px 12px',
                        background: '#f4efe2',
                        border: '1px solid rgba(92,78,51,0.25)',
                        borderRadius: 4,
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 12,
                        color: '#5b5a52',
                        outline: 'none',
                        colorScheme: 'light',
                      }}
                    />
                  </div>
                  {(dateFilters.startDate || dateFilters.endDate) && (
                    <button
                      onClick={clearDateFilters}
                      style={{
                        padding: '8px 14px',
                        background: 'transparent',
                        border: '1px solid rgba(92,78,51,0.35)',
                        borderRadius: 4,
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 9,
                        letterSpacing: '0.15em',
                        textTransform: 'uppercase',
                        color: '#d97706',
                        cursor: 'pointer',
                      }}
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
                {(dateFilters.startDate || dateFilters.endDate) && (
                  <div style={{ marginTop: 10, fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#d97706' }}>
                    {dateFilters.startDate && dateFilters.endDate
                      ? `Showing transactions from ${formatDate(dateFilters.startDate)} to ${formatDate(dateFilters.endDate)}`
                      : dateFilters.startDate
                      ? `Showing transactions from ${formatDate(dateFilters.startDate)} onwards`
                      : `Showing transactions up to ${formatDate(dateFilters.endDate)}`
                    }
                  </div>
                )}
              </div>

              {transactionsLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 128 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    border: '2px solid rgba(92,78,51,0.25)',
                    borderTopColor: '#d97706',
                    animation: 'spin 0.8s linear infinite',
                  }} />
                </div>
              ) : transactionsData?.transactions && transactionsData.transactions.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'rgba(201,165,90,0.02)' }}>
                        <th style={thStyle}>Date</th>
                        <th style={thStyle}>Description</th>
                        <th style={thStyle}>Type</th>
                        <th style={thStyle}>Amount</th>
                        <th style={thStyle}>Reference</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactionsData.transactions.map((transaction: BankTransaction) => (
                        <tr
                          key={transaction.id}
                          style={{ transition: 'background 0.15s' }}
                          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(248,250,252,1)')}
                          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                        >
                          <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                            {formatDate(transaction.date)}
                          </td>
                          <td style={tdStyle}>
                            {transaction.description}
                          </td>
                          <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                            <span style={{
                              ...getTransactionTypeStyle(transaction.type),
                              display: 'inline-block',
                              padding: '3px 10px',
                              borderRadius: 3,
                              fontFamily: "'Inter', sans-serif",
                              fontSize: 8,
                              letterSpacing: '0.15em',
                              textTransform: 'uppercase',
                            }}>
                              {transaction.type}
                            </span>
                          </td>
                          <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                            <span style={{
                              fontFamily: "'Inter', sans-serif",
                              fontSize: 16,
                              fontWeight: 300,
                              color: transaction.type === 'INCOME' ? '#5ac98c' : '#c95a5a',
                            }}>
                              {transaction.type === 'INCOME' ? '+' : '-'}{formatCurrency(transaction.amount)}
                            </span>
                          </td>
                          <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                            {transaction.reference || (
                              <span style={{ color: '#8a8778' }}>—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ padding: 48, textAlign: 'center' }}>
                  <svg width="40" height="40" fill="none" stroke="rgba(217,119,6,0.25)" viewBox="0 0 24 24" style={{ margin: '0 auto 12px' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, letterSpacing: '0.2em', color: '#6b6a5c' }}>
                    {(dateFilters.startDate || dateFilters.endDate)
                      ? 'NO TRANSACTIONS IN SELECTED RANGE'
                      : 'NO TRANSACTIONS FOUND'
                    }
                  </p>
                </div>
              )}

              {/* Pagination */}
              {transactionsData?.pagination && transactionsData.pagination.totalPages > 1 && (
                <div style={{
                  marginTop: 20,
                  paddingTop: 16,
                  borderTop: '1px solid rgba(92,78,51,0.15)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c' }}>
                    Showing {((currentPage - 1) * transactionsData.pagination.itemsPerPage) + 1}–{Math.min(currentPage * transactionsData.pagination.itemsPerPage, transactionsData.pagination.totalItems)} of {transactionsData.pagination.totalItems}
                  </span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      style={{
                        padding: '5px 12px',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 9,
                        letterSpacing: '0.12em',
                        background: 'rgba(239,232,213,0.5)',
                        color: currentPage === 1 ? '#c9c2ab' : '#8a8778',
                        border: '1px solid rgba(92,78,51,0.22)',
                        borderRadius: 3,
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        opacity: currentPage === 1 ? 0.5 : 1,
                      }}
                    >
                      Previous
                    </button>
                    <span style={{
                      padding: '5px 12px',
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 11,
                      color: '#5b5a52',
                    }}>
                      Page {currentPage} of {transactionsData.pagination.totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(transactionsData.pagination.totalPages, prev + 1))}
                      disabled={currentPage === transactionsData.pagination.totalPages}
                      style={{
                        padding: '5px 12px',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 9,
                        letterSpacing: '0.12em',
                        background: 'rgba(239,232,213,0.5)',
                        color: currentPage === transactionsData.pagination.totalPages ? '#c9c2ab' : '#8a8778',
                        border: '1px solid rgba(92,78,51,0.22)',
                        borderRadius: 3,
                        cursor: currentPage === transactionsData.pagination.totalPages ? 'not-allowed' : 'pointer',
                        opacity: currentPage === transactionsData.pagination.totalPages ? 0.5 : 1,
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showAdjustmentForm && (
        <BankAdjustmentForm
          bankAccountId={account.id}
          accountName={account.accountName}
          currentBalance={account.balance}
          onClose={() => setShowAdjustmentForm(false)}
        />
      )}
    </div>
  );
};

export default BankAccountDetail;
